import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { Modal } from '@/ui/Modal/Modal';
import { Flex } from '@/ui/Flex/Flex';
import CustomSwitch from '@/ui/Switch/Switch';
import { Input } from '@/ui/Input/Input';
import { useReadScripts } from '@/features/_data/useScripts';
import checkScriptHomeTimer from '@/utils/checkScriptHomeTimer';
import { T } from '@/ui/Text/Text';

interface SettingsProps {
  isTrihowPad: boolean;
  channelSelective: boolean;
  disallowSlideDeletion: boolean;
  setChannelSelective: React.Dispatch<React.SetStateAction<boolean>>;
  setDisallowSlideDeletion: React.Dispatch<React.SetStateAction<boolean>>;
  languageSupport: boolean;
  setLanguageSupport: React.Dispatch<React.SetStateAction<boolean>>;
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  checkForTagMappings: () => boolean;
  isAdmin: boolean;
  homeTimer: number;
  setHomeTimer: React.Dispatch<React.SetStateAction<number>>;
  unitId: string | undefined;
}

const StyledSaveButton = styled.button`
  padding: 10px 20px;
  border: none;
  width: 100%;
  border-radius: 4px;
  align-self: flex-end;
  background-color: ${({ theme }) => theme.color('#239AE7')};
  color: white;
  cursor: pointer;
  ${({ disabled }) =>
    disabled &&
    css`
      background-color: ${({ theme }) => theme.color('#B2B1BA')};
      pointer-events: none;
    `}
  &:hover {
    background-color: ${({ theme }) => theme.color('#158AD6')};
  }
`;

const StyledControllersWrapper = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  align-self: flex-start;
  width: 100%;
`;

export const StyledFlex = styled.div`
  display: flex;
  width: 100%;
`;

export const StyledSettingRow = styled.div<{ readonly enabled: boolean }>`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

export const StyledSettingTitle = styled.h2`
  user-select: none;
  font-size: 1rem;
  margin-left: 0;
  font-weight: 100;
`;

export const SettingsModal = ({
  unitId,
  homeTimer,
  setHomeTimer,
  isTrihowPad,
  channelSelective,
  setChannelSelective,
  disallowSlideDeletion,
  setDisallowSlideDeletion,
  setLanguageSupport,
  languageSupport,
  setIsModalOpen,
  checkForTagMappings,
  isAdmin,
}: SettingsProps) => {
  const doTagMappingsExist = checkForTagMappings();
  const intl = useIntl();
  const { data: script, isLoading } = useReadScripts(unitId || '');
  const [disableHomeTimer, setDisableHomeTimer] = useState(true);

  useEffect(() => {
    if (isLoading) {
      setDisableHomeTimer(true);
      return;
    }
    setDisableHomeTimer(checkScriptHomeTimer(script?.script));
  }, [script]);

  return (
    <Modal
      styles={[
        css`
          max-width: 440px;
          height: fit-content;
        `,
      ]}
      contentStyles={[
        css`
          height: 100%;
        `,
      ]}
      title="Settings"
      onClose={() => setIsModalOpen(false)}
    >
      <Flex gap="32px 32px" direction="column" align="center" grow="1">
        {isAdmin && (
          <StyledControllersWrapper>
            <StyledSettingRow
              onClick={() => {
                if (doTagMappingsExist) return;
                setChannelSelective((prev) => !prev);
              }}
              enabled={Boolean(channelSelective)}
            >
              <StyledSettingTitle>Channel Selective:</StyledSettingTitle>
              <CustomSwitch
                size="large"
                onChange={() => {}}
                disabled={doTagMappingsExist}
                checked={Boolean(channelSelective)}
              />
            </StyledSettingRow>
          </StyledControllersWrapper>
        )}
        {isAdmin && (
          <StyledSettingRow
            onClick={() => setDisallowSlideDeletion((prev) => !prev)}
            enabled={Boolean(disallowSlideDeletion)}
          >
            <StyledSettingTitle>Disallow Slide Deletion:</StyledSettingTitle>
            <CustomSwitch
              size="large"
              onChange={() => {}}
              checked={Boolean(disallowSlideDeletion)}
            />
          </StyledSettingRow>
        )}

        {isAdmin && (
          <StyledSettingRow
            onClick={() => {
              if (doTagMappingsExist) return;
              setLanguageSupport((prev) => !prev);
            }}
            enabled={Boolean(languageSupport)}
          >
            <StyledSettingTitle>Enable Language Support:</StyledSettingTitle>
            <CustomSwitch
              size="large"
              onChange={() => {}}
              disabled={doTagMappingsExist}
              checked={Boolean(languageSupport)}
            />
          </StyledSettingRow>
        )}
        {!isTrihowPad && (
          <StyledFlex style={{ flexDirection: 'column', gap: '8px' }}>
            <StyledSettingRow enabled={false}>
              <StyledSettingTitle>Home Timer (in seconds):</StyledSettingTitle>
              {isLoading ? (
                'checking script...'
              ) : (
                <Input
                  disabled={disableHomeTimer}
                  type="number"
                  value={String(homeTimer / 1000)}
                  onChange={(e) => setHomeTimer(Number(e) * 1000)}
                />
              )}
            </StyledSettingRow>
            {!isLoading && disableHomeTimer && (
              <T>
                {intl.formatMessage({
                  defaultMessage:
                    'The home timer feature is currently disabled. To enable this feature, please contact Trihow Support.',
                  id: 'GVU1SY',
                })}
              </T>
            )}
          </StyledFlex>
        )}
        <StyledFlex>
          <StyledSaveButton
            type="button"
            onClick={() => {
              setIsModalOpen(false);
            }}
          >
            <FormattedMessage defaultMessage="Close" id="rbrahO" />
          </StyledSaveButton>
        </StyledFlex>
        <StyledSettingTitle>
          {doTagMappingsExist && (
            <FormattedMessage
              defaultMessage="To Enable/Disable Channel Selective/Language Support, make sure you removed all existing mappings"
              id="7sfLX+"
            />
          )}
        </StyledSettingTitle>
      </Flex>
    </Modal>
  );
};
